import React, {useEffect, useState} from 'react';
import {Box, Button, Divider, Drawer, IconButton, InputLabel, Stack, Typography} from '@mui/material';
import {Outlet, useLocation, useNavigate, useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {useSnackbar} from 'notistack';

import {Close} from '@icons/index';
import {Sizes} from '@constants/sizes.constants';
import SelectComponent, {SelectItem} from '@components/inputs/select/Select.component';
import TextFieldComponent from '@components/inputs/textField/TextField.component';
import {finalize, map} from 'rxjs';
import {Account, accountsService, ManageAccount} from '@store/settings/account';
import {TaxRule} from '@store/common/organisation.enums';
import NumberTextFieldComponent from '@components/inputs/numberTextField/NumberTextField.component';
import AsyncSelectComponent from '@components/inputs/asyncSelect/AsyncSelect.component';
import {serviceKindsService} from '@store/settings/serviceKinds/serviceKinds.service';
import DatePickerComponent from '@components/inputs/datePicker/DatePicker.component';

interface ManageAccountDrawerProps {
  isCreation?: boolean;
}

const ManageAccountDrawer = (props: ManageAccountDrawerProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { accountId } = useParams();
  const { isCreation } = props;

  const [loading, setLoading] = useState(false);
  const [account, setAccount] = useState<ManageAccount>({});
  const [waste, setWaste] = useState<SelectItem>({ label: '', value: '' });
  const [serviceKind, setServiceKind] = useState<SelectItem>({ label: '', value: '' });


  const updateAccount = (name: string) => (value: any) => setAccount((state) => ({ ...state, [name]: value }));
  const handleClose = () => navigate('..', { replace: true });

  useEffect(() => {
    const initialStateAccount: Account = location.state as Account;

    if (initialStateAccount) {
      setAccount(initialStateAccount);
      setWaste({ label: initialStateAccount.waste?.name ?? '', value: initialStateAccount.waste?.id ?? '' });
      setServiceKind({ label: initialStateAccount.serviceKind?.kind || '', value: initialStateAccount.serviceKind?.id || '' });
    }
  }, [location.state]);

  useEffect(() => {
    if (accountId) {
      accountsService.getAccountById(accountId).subscribe({
        next: (res) => {
          setAccount(res);
          setWaste({ label: res.waste?.name ?? '', value: res.waste?.id ?? '' });
          setServiceKind({ label: res.serviceKind?.kind || '', value: res.serviceKind?.id || '' });
        },
        error: (err) => enqueueSnackbar(err.text, err.options),
      });
    }
  }, [enqueueSnackbar, accountId]);

  const handleSave = () => {
    delete account.waste;
    delete account.serviceKind;
    setLoading(true);
    if (isCreation) {
      accountsService
        .createAccount({ ...account, wasteId: waste.value, serviceKindId: serviceKind.value })
        .pipe(finalize(() => setLoading(false)))
        .subscribe({
          next: () => {
            enqueueSnackbar(t('settings.account.manage.success'), { variant: 'success' });
            handleClose();
          },
          error: (err) => enqueueSnackbar(err.text, err.options),
        });
    } else {
      if (!accountId) { setLoading(false); return; }
      accountsService
        .updateAccount(accountId, { ...account, wasteId: waste.value, serviceKindId: serviceKind.value })
        .pipe(finalize(() => setLoading(false)))
        .subscribe({
          next: () => {
            enqueueSnackbar(t('settings.account.manage.updated'), { variant: 'success' });
            handleClose();
          },
          error: (err) => enqueueSnackbar(err.text, err.options),
        });
    }
  };

  return (
    <>
      <Drawer anchor="right" open onClose={handleClose}>
        <Stack overflow="auto">
          <Stack direction="row" justifyContent="space-between">
            <Typography fontSize={24} fontWeight="700"> {isCreation ? t('settings.account.manage.title.add') : t('settings.account.manage.title.edit')}</Typography>
            <IconButton onClick={handleClose}>
              <Close />
            </IconButton>
          </Stack>
          <Divider variant="fullWidth" orientation="horizontal" sx={{ paddingTop: '74px' }} />
          <Stack pt="27px" pr="10px" spacing={2}>
            <AsyncSelectComponent
              labelWidth={Sizes.drawerInputLabelWidth}
              label={t('settings.account.fields.serviceKindId')}
              placeholder={t('global.select')}
              getOptions={() => serviceKindsService.getServiceKinds().pipe(
                map((options) => options.data.map((o) => ({ label: o.kind || '-', value: o.id }))))}
              value={serviceKind}
              handleChange={setServiceKind}
              required={true}
            />
            <SelectComponent
              labelWidth={Sizes.drawerInputLabelWidth}
              label={t('settings.account.fields.taxRule')}
              placeholder={t('global.selectOption')}
              items={TaxRule.selectItems()}
              handleChange={updateAccount('taxRule')}
              value={account.taxRule ?? ''}
              required/>
            <TextFieldComponent
              labelWidth={Sizes.drawerInputLabelWidth}
              label={t('settings.account.fields.sellingAccountNumber')}
              placeholder={t('global.selectOption')}
              handleChange={updateAccount('sellingAccountNumber')}
              value={account.sellingAccountNumber ?? ''}
              required />
            <NumberTextFieldComponent
              labelWidth={Sizes.drawerInputLabelWidth}
              label={t('settings.account.fields.sellPctVat')}
              placeholder={t('global.selectOption')}
              handleChange={updateAccount('sellPctVat')}
              pattern={new RegExp(/^(\d*|\d+)[,.]?\d*$/)}
              value={account.sellPctVat}
              required />
            <TextFieldComponent
              labelWidth={Sizes.drawerInputLabelWidth}
              label={t('settings.account.fields.sellVatAccount')}
              placeholder={t('global.selectOption')}
              handleChange={updateAccount('sellVatAccount')}
              value={account.sellVatAccount ?? ''}
              required />
            <TextFieldComponent
              labelWidth={Sizes.drawerInputLabelWidth}
              label={t('settings.account.fields.sellFeeType')}
              placeholder={t('global.selectOption')}
              handleChange={updateAccount('sellFeeType')}
              value={account.sellFeeType ?? ''}
              required />
            <TextFieldComponent
              labelWidth={Sizes.drawerInputLabelWidth}
              label={t('settings.account.fields.buyingAccountNumber')}
              placeholder={t('global.selectOption')}
              handleChange={updateAccount('buyingAccountNumber')}
              value={account.buyingAccountNumber ?? ''}
              required />
            <NumberTextFieldComponent
              labelWidth={Sizes.drawerInputLabelWidth}
              label={t('settings.account.fields.buyPctVat')}
              placeholder={t('global.selectOption')}
              handleChange={updateAccount('buyPctVat')}
              pattern={new RegExp(/^(\d*|\d+)[,.]?\d*$/)}
              value={account.buyPctVat}
              required />
            <TextFieldComponent
              labelWidth={Sizes.drawerInputLabelWidth}
              label={t('settings.account.fields.buyVatAccount')}
              placeholder={t('global.selectOption')}
              handleChange={updateAccount('buyVatAccount')}
              value={account.buyVatAccount ?? ''}
              required />
            <TextFieldComponent
              labelWidth={Sizes.drawerInputLabelWidth}
              label={t('settings.account.fields.buyFeeType')}
              placeholder={t('global.selectOption')}
              handleChange={updateAccount('buyFeeType')}
              value={account.buyFeeType ?? ''}
              required />
            <Stack direction="row" justifyContent="space-between" spacing={2}>
              <InputLabel sx={{width: Sizes.drawerInputLabelWidth}}>
                {t('settings.account.fields.validity')}*
              </InputLabel>
              <Box flex={0.5}>
                <DatePickerComponent
                  placeholder={t('global.selectDate')}
                  value={account.validFromDate}
                  inputWidth={120}
                  handleChange={(validFromDate) => {
                    updateAccount('validFromDate')(validFromDate);
                  }}
                />
              </Box>
              <Box flex={0.5}>
                <DatePickerComponent
                  placeholder={t('global.selectDate')}
                  value={account.validToDate}
                  inputWidth={120}
                  handleChange={(validToDate) => {
                    updateAccount('validToDate')(validToDate);
                  }}/>
              </Box>
            </Stack>
          </Stack>
          <Divider variant="fullWidth" orientation="horizontal" sx={{ paddingTop: '32px' }} />
          <Stack alignSelf="flex-end" pb="25px" pt="53px" pr="10px">
            <Button variant="contained" color="primary" disabled={loading} onClick={handleSave}>
              {t('global.save')}
            </Button>
          </Stack>
        </Stack>
      </Drawer>
      <Outlet />
    </>
  );
};

export default ManageAccountDrawer;
